<script lang="ts" setup>
// Core
import { computed } from "vue";

// Components
import Popover, { PopoverProps } from "@/components/core/PopoverComponent.vue";

const props = defineProps<{
  iconClass?: string | string[];
  iconColor?: string;
  iconSize?: string;
  iconSrc?: string;
  isTooltip?: boolean;
  action?: (param?: any) => any;
  disabled?: boolean;
  hover?: "full" | "icon" | "padding" | false;
  active?: "full" | "icon" | "padding" | false;
  useTheme?: boolean;
  popover?: PopoverProps;
  autoHeigth?: boolean;
  passEvent?: boolean;
}>();

const style = computed(() => ({
  width: props.iconSrc ? props.iconSize || "" : "",
  minWidth: props.iconSrc ? props.iconSize || "" : "",
  height: props.autoHeigth ? "auto" : props.iconSrc ? props.iconSize || "" : "",
  color: props.iconColor,
  fontSize: !props.iconSrc ? props.iconSize || "" : "16px",
  cursor: props.action ? "pointer" : "default"
}));
</script>

<template>
  <Popover v-bind="popover" :disabled="!popover">
    <template #reference>
      <inline-svg
        v-if="iconSrc"
        :class="[
          iconClass,
          !action && !isTooltip && 'not-clickable',
          hover && (hover === 'full' ? 'hover-full' : hover === 'padding' ? 'hover-padding' : 'hover-icon'),
          active && (active === 'full' ? 'active-full' : active === 'padding' ? 'active-padding' : 'active-icon'),
          useTheme && 'theme',
          disabled && 'disabled'
        ]"
        :src="iconSrc"
        :style="style"
        @click="
          (event: Event) => {
            if (passEvent && !disabled) {
              action?.(event);
            } else if (!disabled) {
              action?.();
            }
          }
        "
      />
      <i
        v-else
        :class="[
          iconClass,
          !action && !isTooltip && 'not-clickable',
          hover && (hover === 'full' ? 'hover-full' : hover === 'padding' ? 'hover-padding' : 'hover-icon'),
          active && (active === 'full' ? 'active-full' : active === 'padding' ? 'active-padding' : 'active-icon'),
          useTheme && 'theme',
          disabled && 'disabled'
        ]"
        :style="style"
        @click="
          (event: Event) => {
            if (passEvent && !disabled) {
              action?.(event);
            } else if (!disabled) {
              action?.();
            }
          }
        "
      />
    </template>
    <template v-if="popover" #children>
      <slot name="children" />
    </template>
  </Popover>
</template>

<style lang="scss" scoped>
.not-clickable {
  pointer-events: none;
}

.theme:not(.disabled) {
  &:not(:hover):not(.active-full):not(.active-icon) {
    :deep(path) {
      @include svg-path-styles($primary_6);
    }
  }
}

.hover-full:not(.disabled) {
  border-radius: $br_sm;
  transition: $transition_duration_fast;

  &:hover {
    background-color: $new-primary_7;

    :deep(path) {
      @include svg-path-styles(white);
    }
  }
}

.hover-padding:not(.disabled) {
  border-radius: $br_xsm;
  padding: $spacing_micro;
  transition: $transition_duration_fast;

  &:hover {
    background-color: $new-primary_7;

    :deep(path) {
      @include svg-path-styles(white);
    }
  }
}

.hover-icon:not(.disabled) {
  border-radius: $br_sm;
  transition: $transition_duration_fast;

  &:hover {
    background-color: $dropdown_fill_hover;

    :deep(path) {
      @include svg-path-styles($primary_6);
    }
  }
}

.active-full:not(.disabled) {
  border-radius: $br_sm;
  background-color: $primary_4;

  :deep(path) {
    @include svg-path-styles(white);
  }
}

.active-padding:not(.disabled) {
  border-radius: $br_xsm;
  padding: $spacing_micro;
  background-color: $primary_4;

  :deep(path) {
    @include svg-path-styles(white);
  }
}

.active-icon:not(.disabled) {
  :deep(path) {
    @include svg-path-styles($primary_4);
  }
}

.disabled {
  :deep(path),
  :deep(circle) {
    @include svg-path-styles($new-neutral_10);
    cursor: default;
  }
}
</style>
